import { useState } from "react";
import { Loader, Trash } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

interface DeleteButtonProps {
  isLoading?: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
}

const DeleteButton = ({
  isLoading,
  disabled,
  onClick,
  className,
}: DeleteButtonProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const handleConfirm = () => {
    setOpen(false);
    onClick();
  };

  const trigger = (
    <button
      disabled={disabled || isLoading}
      className={cn(
        "cursor-pointer whitespace-nowrap shrink-0 [&_svg]:shrink-0 outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 py-2 px-2 bg-primary/10 text-sm focus:bg-primary/10! focus:text-primary! text-primary hover:bg-primary/20 rounded-md flex items-center gap-2",
        className,
      )}
    >
      {isLoading ? (
        <Loader className="animate-spin text-primary" />
      ) : (
        <Trash className="text-primary" />
      )}
      {t("keys.delete")}
    </button>
  );

  const title = t("keys.confirm_delete.title");
  const description = t("keys.confirm_delete.description");

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <DrawerFooter className="pt-2">
            <Button variant="destructive" onClick={handleConfirm}>
              {t("keys.confirm")}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline">{t("keys.cancel")}</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("keys.cancel")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={handleConfirm}>
            {t("keys.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteButton;
